/**
 * University Detail Page - Soft Professional Design
 * Clean, scrollable layout with sticky sidebar navigation
 */

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import {
  Loader2, ArrowLeft, ExternalLink, Users, DollarSign, GraduationCap,
  MapPin, Heart, Home, Briefcase, Cloud, Plane, Coffee,
  TrendingUp, BookOpen, Globe, Award, Shield, Wifi, Bus, ChevronRight
} from 'lucide-react';
import { universityAPI } from '../api/university-api';
import { SEO } from '../../../shared/components';
import type { University } from '../types';

// Soft professional color palette
const colors = {
  primary: '#5b75f8',      // Soft blue
  secondary: '#6b7280',    // Soft gray
  accent: '#f59e0b',       // Soft amber
  success: '#10b981',      // Soft green
  info: '#3b82f6',         // Soft blue
  purple: '#8b5cf6',       // Soft purple
  orange: '#f97316',       // Soft orange
  background: '#fafbfc',   // Very light gray
  card: '#ffffff',         // White
  border: '#e5e7eb',       // Light gray
  text: '#1e293b',         // Dark slate
  textMuted: '#64748b',    // Muted slate
  textLight: '#94a3b8'     // Light slate
};

const UniversityDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [university, setUniversity] = useState<University | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('overview');

  // Refs for scroll sections
  const overviewRef = useRef<HTMLDivElement>(null);
  const programsRef = useRef<HTMLDivElement>(null);
  const costsRef = useRef<HTMLDivElement>(null);
  const lifeRef = useRef<HTMLDivElement>(null);
  const internationalRef = useRef<HTMLDivElement>(null);

  const backUrl = location.state?.from || '/university/browse';

  const navItems = [
    { id: 'overview', label: 'Overview', icon: Home, ref: overviewRef },
    { id: 'programs', label: 'Programs', icon: GraduationCap, ref: programsRef },
    { id: 'costs', label: 'Costs & Living', icon: DollarSign, ref: costsRef },
    { id: 'life', label: 'Campus Life', icon: Coffee, ref: lifeRef },
    { id: 'international', label: 'International', icon: Globe, ref: internationalRef },
  ];

  useEffect(() => {
    const loadUniversity = async () => {
      if (!slug) {
        setError('No university specified');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await universityAPI.getUniversityById(slug as any);
        setUniversity(data);
      } catch (err: any) {
        console.error('Failed to load university:', err);
        setError(err.message || 'Failed to load university');
      } finally {
        setIsLoading(false);
      }
    };

    loadUniversity();
  }, [slug]);

  // Scroll spy to update active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => ({
        id: item.id,
        element: item.ref.current
      }));

      const scrollPosition = window.scrollY + 150;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element && section.element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [university]);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.background }}>
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.primary }} />
          <span style={{ color: colors.text }}>Loading university details...</span>
        </div>
      </div>
    );
  }

  if (error || !university) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.background }}>
        <div className="text-center">
          <p className="mb-4" style={{ color: '#ef4444' }}>{error || 'University not found'}</p>
          <Button onClick={() => navigate(backUrl)} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${university.name} - Complete University Guide`}
        description={`Discover ${university.name} - Programs, costs, student life, climate, and everything you need to know`}
      />

      <div style={{ minHeight: '100vh', background: colors.background }}>
        {/* Hero Section with Background Image */}
        <div style={{
          background: '#4a5568',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Image */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1562774053-701939374585?w=1200)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.25
          }}></div>

          {/* Overlay for readability */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(74, 85, 104, 0.85), rgba(45, 55, 72, 0.95))'
          }}></div>

          <div className="container mx-auto px-4 py-8 relative z-10">
            <button
              onClick={() => navigate(backUrl)}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '24px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Browse</span>
            </button>

            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
              <div className="flex-1">
                <h1 style={{
                  fontSize: '3rem',
                  fontWeight: '700',
                  marginBottom: '20px',
                  lineHeight: '1.1'
                }}>
                  {university.name}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    padding: '10px 20px',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.25)'
                  }}>
                    <span style={{ fontSize: '24px' }}>{university.country.flag_emoji}</span>
                    <span style={{ fontWeight: '600', fontSize: '15px' }}>{university.country.name}</span>
                  </div>

                  {university.city && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '10px 20px',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.25)'
                    }}>
                      <MapPin className="h-4 w-4" />
                      <span style={{ fontWeight: '500', fontSize: '15px' }}>{university.city}</span>
                    </div>
                  )}

                  {university.qs_world_ranking && (
                    <div style={{
                      background: colors.accent,
                      padding: '10px 20px',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Award className="h-4 w-4" />
                      <span style={{ fontWeight: '600', fontSize: '15px' }}>#{university.qs_world_ranking} QS World</span>
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 max-w-xl">
                  {university.total_students && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      padding: '16px',
                      borderRadius: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>Students</div>
                      <div style={{ fontSize: '22px', fontWeight: '700' }}>
                        {(university.total_students / 1000).toFixed(1)}k
                      </div>
                    </div>
                  )}

                  {university.acceptance_rate && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      padding: '16px',
                      borderRadius: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>Acceptance</div>
                      <div style={{ fontSize: '22px', fontWeight: '700' }}>
                        {university.acceptance_rate}%
                      </div>
                    </div>
                  )}

                  {university.international_students && (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      padding: '16px',
                      borderRadius: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                      <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>International</div>
                      <div style={{ fontSize: '22px', fontWeight: '700' }}>
                        {(university.international_students / 1000).toFixed(1)}k
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3" style={{ minWidth: '200px' }}>
                <button style={{
                  background: 'white',
                  color: colors.primary,
                  padding: '14px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}>
                  <Heart className="h-5 w-5" />
                  <span>Save University</span>
                </button>

                {university.website_url && (
                  <a
                    href={university.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                      color: 'white',
                      padding: '14px 24px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      textDecoration: 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    }}
                  >
                    <ExternalLink className="h-5 w-5" />
                    <span>Visit Website</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area with Sidebar Navigation */}
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-12 gap-8">
            {/* Sticky Sidebar Navigation */}
            <div className="col-span-12 lg:col-span-3">
              <div style={{
                position: 'sticky',
                top: '20px',
                background: colors.card,
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                border: `1px solid ${colors.border}`
              }}>
                <h3 style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '16px'
                }}>
                  Navigation
                </h3>

                <nav style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;

                    return (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.ref)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          border: 'none',
                          background: isActive ? colors.primary : 'transparent',
                          color: isActive ? 'white' : colors.textMuted,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          fontWeight: isActive ? '600' : '500',
                          fontSize: '14px',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = '#f1f5f9';
                            e.currentTarget.style.color = colors.text;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = colors.textMuted;
                          }
                        }}
                      >
                        <Icon className="h-5 w-5" style={{ flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>{item.label}</span>
                        {isActive && <ChevronRight className="h-4 w-4" />}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="col-span-12 lg:col-span-9">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>

                {/* Overview Section */}
                <section ref={overviewRef} id="overview">
                  <h2 style={{
                    fontSize: '26px',
                    fontWeight: '700',
                    color: colors.text,
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: colors.primary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Home className="h-5 w-5" style={{ color: 'white' }} />
                    </div>
                    Overview
                  </h2>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div style={{
                      background: colors.card,
                      borderRadius: '12px',
                      padding: '24px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      border: `1px solid ${colors.border}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '52px',
                          height: '52px',
                          borderRadius: '10px',
                          background: colors.info,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Users className="h-6 w-6" style={{ color: 'white' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: colors.text }}>
                            {university.total_students?.toLocaleString() || 'N/A'}
                          </div>
                          <div style={{ fontSize: '13px', color: colors.textMuted }}>Total Students</div>
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: colors.card,
                      borderRadius: '12px',
                      padding: '24px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      border: `1px solid ${colors.border}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '52px',
                          height: '52px',
                          borderRadius: '10px',
                          background: colors.success,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <TrendingUp className="h-6 w-6" style={{ color: 'white' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: colors.text }}>
                            {university.acceptance_rate || 'N/A'}%
                          </div>
                          <div style={{ fontSize: '13px', color: colors.textMuted }}>Acceptance Rate</div>
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: colors.card,
                      borderRadius: '12px',
                      padding: '24px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      border: `1px solid ${colors.border}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '52px',
                          height: '52px',
                          borderRadius: '10px',
                          background: colors.purple,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Globe className="h-6 w-6" style={{ color: 'white' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: colors.text }}>
                            {university.international_students?.toLocaleString() || 'N/A'}
                          </div>
                          <div style={{ fontSize: '13px', color: colors.textMuted }}>International Students</div>
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: colors.card,
                      borderRadius: '12px',
                      padding: '24px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      border: `1px solid ${colors.border}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '52px',
                          height: '52px',
                          borderRadius: '10px',
                          background: colors.accent,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Award className="h-6 w-6" style={{ color: 'white' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: colors.text }}>
                            #{university.qs_world_ranking || 'N/A'}
                          </div>
                          <div style={{ fontSize: '13px', color: colors.textMuted }}>QS World Ranking</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Info */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '12px',
                    padding: '28px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${colors.border}`
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: colors.text,
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <MapPin className="h-5 w-5" style={{ color: colors.primary }} />
                      Location & Campus
                    </h3>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 style={{ fontSize: '13px', fontWeight: '700', color: colors.textMuted, marginBottom: '12px' }}>
                          Campus Details
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: colors.textLight, fontSize: '14px' }}>City</span>
                            <span style={{ fontWeight: '600', color: colors.text, fontSize: '14px' }}>{university.city || 'N/A'}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: colors.textLight, fontSize: '14px' }}>Region</span>
                            <span style={{ fontWeight: '600', color: colors.text, fontSize: '14px' }}>{university.state_province || 'N/A'}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: colors.textLight, fontSize: '14px' }}>Country</span>
                            <span style={{ fontWeight: '600', color: colors.text, fontSize: '14px' }}>
                              {university.country.flag_emoji} {university.country.name}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: colors.textLight, fontSize: '14px' }}>Type</span>
                            <span style={{ fontWeight: '600', color: colors.text, fontSize: '14px' }}>{university.university_type || 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 style={{ fontSize: '13px', fontWeight: '700', color: colors.textMuted, marginBottom: '12px' }}>
                          Quick Facts
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                          <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                            <Wifi className="h-5 w-5" style={{ color: colors.primary, marginTop: '2px' }} />
                            <div>
                              <div style={{ fontWeight: '600', color: colors.text, fontSize: '14px' }}>Modern Campus</div>
                              <div style={{ fontSize: '13px', color: colors.textLight }}>State-of-the-art facilities</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                            <Bus className="h-5 w-5" style={{ color: colors.primary, marginTop: '2px' }} />
                            <div>
                              <div style={{ fontWeight: '600', color: colors.text, fontSize: '14px' }}>Public Transport</div>
                              <div style={{ fontSize: '13px', color: colors.textLight }}>Well connected</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                            <Shield className="h-5 w-5" style={{ color: colors.primary, marginTop: '2px' }} />
                            <div>
                              <div style={{ fontWeight: '600', color: colors.text, fontSize: '14px' }}>Safe Campus</div>
                              <div style={{ fontSize: '13px', color: colors.textLight }}>24/7 security</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Programs Section */}
                <section ref={programsRef} id="programs">
                  <h2 style={{
                    fontSize: '26px',
                    fontWeight: '700',
                    color: colors.text,
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: colors.info,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <GraduationCap className="h-5 w-5" style={{ color: 'white' }} />
                    </div>
                    Available Programs ({university.programs?.length || 0})
                  </h2>

                  {university.programs && university.programs.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {university.programs.map((program: any) => (
                        <div
                          key={program.id}
                          style={{
                            background: colors.card,
                            borderRadius: '12px',
                            padding: '22px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                            border: `1px solid ${colors.border}`,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div style={{ flex: 1 }}>
                              <h3 style={{ fontSize: '17px', fontWeight: '700', color: colors.text, marginBottom: '10px' }}>
                                {program.name}
                              </h3>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {program.field && (
                                  <span style={{
                                    background: '#ede9fe',
                                    color: colors.purple,
                                    padding: '5px 12px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <BookOpen className="h-3 w-3" />
                                    {program.field}
                                  </span>
                                )}
                                {program.degree_type && (
                                  <span style={{
                                    background: '#f1f5f9',
                                    color: colors.textMuted,
                                    padding: '5px 12px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600'
                                  }}>
                                    {program.degree_type}
                                  </span>
                                )}
                                {program.duration_years && (
                                  <span style={{
                                    background: '#f1f5f9',
                                    color: colors.textMuted,
                                    padding: '5px 12px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600'
                                  }}>
                                    {program.duration_years} years
                                  </span>
                                )}
                              </div>
                            </div>

                            {program.tuition_usd && (
                              <div style={{
                                background: colors.primary,
                                padding: '18px 24px',
                                borderRadius: '10px',
                                textAlign: 'right',
                                minWidth: '160px'
                              }}>
                                <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '4px' }}>
                                  Annual Tuition
                                </div>
                                <div style={{ fontSize: '32px', fontWeight: '800', color: 'white', lineHeight: '1' }}>
                                  ${(program.tuition_usd / 1000).toFixed(0)}k
                                </div>
                                <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.85)', marginTop: '4px' }}>
                                  USD per year
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      background: colors.card,
                      borderRadius: '12px',
                      padding: '60px 32px',
                      textAlign: 'center',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                      border: `1px solid ${colors.border}`
                    }}>
                      <GraduationCap className="h-16 w-16 mx-auto mb-4" style={{ color: colors.textLight, opacity: 0.4 }} />
                      <p style={{ color: colors.textMuted, fontSize: '16px' }}>No program information available</p>
                      <p style={{ fontSize: '14px', color: colors.textLight, marginTop: '8px' }}>Contact the university for program details</p>
                    </div>
                  )}
                </section>

                {/* Costs Section */}
                <section ref={costsRef} id="costs">
                  <h2 style={{
                    fontSize: '26px',
                    fontWeight: '700',
                    color: colors.text,
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: colors.success,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <DollarSign className="h-5 w-5" style={{ color: 'white' }} />
                    </div>
                    Costs & Living Expenses
                  </h2>

                  {/* Tuition */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '12px',
                    padding: '28px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${colors.border}`,
                    marginBottom: '16px'
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: colors.text,
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <DollarSign className="h-5 w-5" style={{ color: colors.success }} />
                      Tuition Fees
                    </h3>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div style={{
                        background: colors.success,
                        padding: '28px',
                        borderRadius: '10px',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '8px' }}>
                          Tuition Range (Annual)
                        </div>
                        <div style={{ fontSize: '42px', fontWeight: '800', lineHeight: '1', marginBottom: '4px' }}>
                          ${university.tuition_usd_min ? (university.tuition_usd_min / 1000).toFixed(0) : '?'}k
                          {university.tuition_usd_max && ` - ${(university.tuition_usd_max / 1000).toFixed(0)}k`}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.85 }}>USD per year</div>
                      </div>

                      <div>
                        <h4 style={{ fontSize: '13px', fontWeight: '700', color: colors.textMuted, marginBottom: '14px' }}>
                          What's Included
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.success }}></div>
                            <span style={{ color: colors.textMuted, fontSize: '14px' }}>Academic instruction</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.success }}></div>
                            <span style={{ color: colors.textMuted, fontSize: '14px' }}>Library & lab access</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.success }}></div>
                            <span style={{ color: colors.textMuted, fontSize: '14px' }}>Student services</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.accent }}></div>
                            <span style={{ color: colors.textMuted, fontSize: '14px' }}>Housing (separate cost)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Living Costs */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '12px',
                    padding: '28px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${colors.border}`
                  }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: colors.text, marginBottom: '8px' }}>
                      Estimated Living Costs
                    </h3>
                    <p style={{ fontSize: '13px', color: colors.textLight, marginBottom: '20px' }}>
                      Monthly expenses in {university.city || university.country.name}
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '14px',
                        borderBottom: `1px solid ${colors.border}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Home className="h-5 w-5" style={{ color: colors.textMuted }} />
                          <span style={{ fontSize: '14px', color: colors.text }}>Housing (Shared)</span>
                        </div>
                        <span style={{ fontSize: '15px', fontWeight: '700', color: colors.text }}>$400 - $800</span>
                      </div>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '14px',
                        borderBottom: `1px solid ${colors.border}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Coffee className="h-5 w-5" style={{ color: colors.textMuted }} />
                          <span style={{ fontSize: '14px', color: colors.text }}>Food & Groceries</span>
                        </div>
                        <span style={{ fontSize: '15px', fontWeight: '700', color: colors.text }}>$300 - $500</span>
                      </div>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '14px',
                        borderBottom: `1px solid ${colors.border}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Bus className="h-5 w-5" style={{ color: colors.textMuted }} />
                          <span style={{ fontSize: '14px', color: colors.text }}>Transportation</span>
                        </div>
                        <span style={{ fontSize: '15px', fontWeight: '700', color: colors.text }}>$50 - $150</span>
                      </div>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '14px',
                        borderBottom: `1px solid ${colors.border}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Wifi className="h-5 w-5" style={{ color: colors.textMuted }} />
                          <span style={{ fontSize: '14px', color: colors.text }}>Internet & Phone</span>
                        </div>
                        <span style={{ fontSize: '15px', fontWeight: '700', color: colors.text }}>$50 - $100</span>
                      </div>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '18px',
                        background: colors.success,
                        borderRadius: '10px',
                        marginTop: '6px'
                      }}>
                        <span style={{ fontSize: '16px', fontWeight: '700', color: 'white' }}>Total Monthly (Estimated)</span>
                        <span style={{ fontSize: '28px', fontWeight: '800', color: 'white' }}>$800 - $1,550</span>
                      </div>
                    </div>

                    <p style={{ fontSize: '11px', color: colors.textLight, marginTop: '14px' }}>
                      * Costs are approximate and may vary based on lifestyle and location within the city
                    </p>
                  </div>
                </section>

                {/* Campus Life Section */}
                <section ref={lifeRef} id="life">
                  <h2 style={{
                    fontSize: '26px',
                    fontWeight: '700',
                    color: colors.text,
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: colors.orange,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Coffee className="h-5 w-5" style={{ color: 'white' }} />
                    </div>
                    Campus Life & Climate
                  </h2>

                  {/* Climate */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '12px',
                    padding: '28px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${colors.border}`,
                    marginBottom: '16px'
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: colors.text,
                      marginBottom: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Cloud className="h-5 w-5" style={{ color: colors.info }} />
                      Climate & Weather
                    </h3>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div style={{
                        background: colors.info,
                        padding: '22px',
                        borderRadius: '10px',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '6px' }}>Climate Type</div>
                        <div style={{ fontSize: '20px', fontWeight: '700' }}>
                          {university.country.name === 'Canada' ? 'Continental' : 'Temperate'}
                        </div>
                      </div>

                      <div style={{
                        background: colors.orange,
                        padding: '22px',
                        borderRadius: '10px',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '6px' }}>Summer</div>
                        <div style={{ fontSize: '20px', fontWeight: '700' }}>20-30°C</div>
                        <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '4px' }}>Warm & Pleasant</div>
                      </div>

                      <div style={{
                        background: '#06b6d4',
                        padding: '22px',
                        borderRadius: '10px',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '6px' }}>Winter</div>
                        <div style={{ fontSize: '20px', fontWeight: '700' }}>-10-5°C</div>
                        <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '4px' }}>Cold & Snowy</div>
                      </div>
                    </div>

                    <p style={{ fontSize: '13px', color: colors.textMuted, marginTop: '18px' }}>
                      Four distinct seasons. Pack warm clothing for winter months!
                    </p>
                  </div>

                  {/* Facilities */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '12px',
                    padding: '28px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${colors.border}`
                  }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: colors.text, marginBottom: '20px' }}>
                      Campus Life & Facilities
                    </h3>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 style={{ fontSize: '13px', fontWeight: '700', color: colors.textMuted, marginBottom: '14px' }}>
                          Student Facilities
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {['Libraries & Study Spaces', 'Sports & Recreation Centers', 'Student Clubs & Societies', 'Health & Wellness Services', 'Career Counseling'].map((facility) => (
                            <div key={facility} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: colors.primary }}></div>
                              <span style={{ fontSize: '14px', color: colors.text }}>{facility}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 style={{ fontSize: '13px', fontWeight: '700', color: colors.textMuted, marginBottom: '14px' }}>
                          Campus Amenities
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {['Dining Halls & Cafeterias', 'On-Campus Housing', 'Transportation Services', 'Campus Security 24/7', 'High-Speed WiFi'].map((amenity) => (
                            <div key={amenity} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: colors.success }}></div>
                              <span style={{ fontSize: '14px', color: colors.text }}>{amenity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* International Students Section */}
                <section ref={internationalRef} id="international">
                  <h2 style={{
                    fontSize: '26px',
                    fontWeight: '700',
                    color: colors.text,
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: colors.purple,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Globe className="h-5 w-5" style={{ color: 'white' }} />
                    </div>
                    International Students
                  </h2>

                  {/* Support Services */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '12px',
                    padding: '28px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${colors.border}`,
                    marginBottom: '16px'
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: colors.text,
                      marginBottom: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Plane className="h-5 w-5" style={{ color: colors.purple }} />
                      International Student Support
                    </h3>

                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 style={{ fontSize: '13px', fontWeight: '700', color: colors.textMuted, marginBottom: '14px' }}>
                          Student Services
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                          <div style={{ display: 'flex', alignItems: 'start', gap: '14px' }}>
                            <div style={{
                              width: '44px',
                              height: '44px',
                              borderRadius: '8px',
                              background: '#ede9fe',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Globe className="h-5 w-5" style={{ color: colors.purple }} />
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: colors.text, fontSize: '14px', marginBottom: '2px' }}>International Office</div>
                              <div style={{ fontSize: '13px', color: colors.textMuted }}>Dedicated support for international students</div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'start', gap: '14px' }}>
                            <div style={{
                              width: '44px',
                              height: '44px',
                              borderRadius: '8px',
                              background: '#dcfce7',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Briefcase className="h-5 w-5" style={{ color: colors.success }} />
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: colors.text, fontSize: '14px', marginBottom: '2px' }}>Work Permit Support</div>
                              <div style={{ fontSize: '13px', color: colors.textMuted }}>Help with part-time work authorization</div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'start', gap: '14px' }}>
                            <div style={{
                              width: '44px',
                              height: '44px',
                              borderRadius: '8px',
                              background: '#dbeafe',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Users className="h-5 w-5" style={{ color: colors.info }} />
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: colors.text, fontSize: '14px', marginBottom: '2px' }}>Student Community</div>
                              <div style={{ fontSize: '13px', color: colors.textMuted }}>Active Indian student associations</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 style={{ fontSize: '13px', fontWeight: '700', color: colors.textMuted, marginBottom: '14px' }}>
                          For Indian Students
                        </h4>
                        <div style={{
                          background: '#fff7ed',
                          padding: '20px',
                          borderRadius: '10px',
                          border: '1px solid #fed7aa',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.orange }}></div>
                            <span style={{ fontSize: '14px', color: colors.text }}>Indian food options available on/near campus</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.orange }}></div>
                            <span style={{ fontSize: '14px', color: colors.text }}>Active Indian Student Association</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.orange }}></div>
                            <span style={{ fontSize: '14px', color: colors.text }}>Diwali & Holi celebrations organized</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.orange }}></div>
                            <span style={{ fontSize: '14px', color: colors.text }}>Cricket & kabaddi clubs available</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Part-time Work */}
                  <div style={{
                    background: colors.card,
                    borderRadius: '12px',
                    padding: '28px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${colors.border}`
                  }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: colors.text,
                      marginBottom: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Briefcase className="h-5 w-5" style={{ color: colors.success }} />
                      Part-Time Work Opportunities
                    </h3>

                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div style={{
                        background: colors.success,
                        padding: '20px',
                        borderRadius: '10px',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '6px' }}>Work Hours Allowed</div>
                        <div style={{ fontSize: '24px', fontWeight: '800' }}>20 hrs/week</div>
                        <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '4px' }}>During semester</div>
                      </div>

                      <div style={{
                        background: colors.info,
                        padding: '20px',
                        borderRadius: '10px',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '6px' }}>Average Hourly Rate</div>
                        <div style={{ fontSize: '24px', fontWeight: '800' }}>$15-25</div>
                        <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '4px' }}>CAD per hour</div>
                      </div>

                      <div style={{
                        background: colors.purple,
                        padding: '20px',
                        borderRadius: '10px',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '6px' }}>Monthly Earnings</div>
                        <div style={{ fontSize: '24px', fontWeight: '800' }}>$1,200-2,000</div>
                        <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '4px' }}>Estimated</div>
                      </div>
                    </div>

                    <div>
                      <h4 style={{ fontSize: '13px', fontWeight: '700', color: colors.textMuted, marginBottom: '14px' }}>
                        Popular Job Options
                      </h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          'Campus Library Assistant',
                          'Teaching Assistant (TA)',
                          'Retail & Hospitality',
                          'Food Service',
                          'Research Assistant',
                          'Tutoring'
                        ].map((job) => (
                          <div key={job} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '14px',
                            background: '#f8f9fc',
                            borderRadius: '8px',
                            border: `1px solid ${colors.border}`
                          }}>
                            <Briefcase className="h-5 w-5" style={{ color: colors.primary }} />
                            <span style={{ fontSize: '14px', color: colors.text }}>{job}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UniversityDetailPage;
